/* eslint-disable max-lines */
import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import {
  MFA_OAUTH_URL,
  MULTI_FACTOR_EndpointKey
} from '../../../../constants/login/login-remember-me-constant';
import { HttpRequestMethods, IDP_APP } from '../../../../constants/utils';
import {
  LoginAuthorizationOperationTypes,
  LoginOAuthParams,
  LoginOAuthResponse
} from '../../../../models/login';
import { HttpHeadersPayload, ReplaceItem } from '../../../../models/utils';
import { X_EAM_AUTHN_STATE } from '../../../../types/constants';
import { LoginRememberMeUtilsService } from '../../../../utils/login/login-remember-me-utils.service';
import { ApiBuilder, AppConfigService, handleError } from '../../../utils';

@Injectable({
  providedIn: 'root'
})
export class LoginAuthorizationService {
  _errorHandler = handleError.bind(this);

  constructor(
    private http: HttpClient,
    private utils: LoginRememberMeUtilsService,
    private appConfig: AppConfigService
  ) {}

  getEamHeaderNameEnv(loginOAuthParams: LoginOAuthParams): string {
    if (
      loginOAuthParams.oauthConfig &&
      loginOAuthParams.oauthConfig.X_EAM_AUTHN_STATE_ENV
    )
      return loginOAuthParams.oauthConfig.X_EAM_AUTHN_STATE_ENV;
    else return X_EAM_AUTHN_STATE;
  }

  loginAuthorization = (
    loginOAuthParams: LoginOAuthParams,
    eamAuthStateValue?: string,
    forcePromptLoginFlow = false,
    enableRBA = false
  ): Observable<LoginOAuthResponse> => {
    let initiateAuthorizationHeader: HttpHeadersPayload = {};
    if (eamAuthStateValue && eamAuthStateValue !== '') {
      const eamHeaderName = this.getEamHeaderNameEnv(loginOAuthParams);
      initiateAuthorizationHeader = {
        'Content-Type':
          'application/vnd.pingidentity.initiateOAuthAuthorization+json',
        'X-XSRF-Header': 'PingFederate',
        [eamHeaderName]: eamAuthStateValue,
        rejectUnauthorized: 'false'
      };
    } else {
      initiateAuthorizationHeader = {
        'Content-Type':
          'application/vnd.pingidentity.initiateOAuthAuthorization+json',
        'X-XSRF-Header': 'PingFederate',
        rejectUnauthorized: 'false'
      };
    }

    const loginAuthorizationUrl: ReplaceItem[] = [
      {
        src: MFA_OAUTH_URL,
        target: this.getUrlBasedOnStorageValue(
          loginOAuthParams,
          forcePromptLoginFlow,
          enableRBA
        )
      }
    ];

    return new ApiBuilder<undefined, LoginOAuthResponse>(
      this.http,
      this.appConfig
    )
      .app(IDP_APP.COMMON)
      .endpointKey(MULTI_FACTOR_EndpointKey.MFA_OAUTH_URL)
      .overrideEndpoint(loginAuthorizationUrl)
      .method(HttpRequestMethods['GET_HEADERS'])
      .timeOut(loginOAuthParams.oauthConfig.LOGIN_TIMEOUT ?? 10000)
      .httpHeaders(initiateAuthorizationHeader)
      .errorHandler(this._errorHandler)
      .library('LOGIN_AUTHORIZATION_SERVICE')
      .withCredentials(true)
      .create();
  };

  getUrlBasedOnStorageValue(
    loginOAuthParams: LoginOAuthParams,
    forcePromptLoginFlow = false,
    enableRBA = false
  ): string {
    const rbaUrl = this.getUrlBasedOnRba(
      loginOAuthParams,
      enableRBA,
      forcePromptLoginFlow
    );

    if (rbaUrl) return rbaUrl;

    return this.getUrlBasedOnStorageCredentials(
      loginOAuthParams,
      forcePromptLoginFlow
    );
  }

  getUrlBasedOnStorageCredentials(
    loginOAuthParams: LoginOAuthParams,
    forcePromptLoginFlow: boolean
  ): string {
    const hasStoredCredentials =
      this.utils.getLocalStorageValue('encyToken') &&
      this.utils.getLocalStorageValue('uid');
    if (hasStoredCredentials) {
      return this.getLoginAuthorizationUrl(
        loginOAuthParams,
        loginOAuthParams.prompt ? 'PROMPT' : 'DEFAULT'
      );
    }
    return this.getUrlBasedOnStorageValueHelper(
      loginOAuthParams,
      forcePromptLoginFlow
    );
  }

  enableRbaCheck(
    enableRBA: boolean,
    loginOAuthParams: LoginOAuthParams
  ): boolean {
    return (
      enableRBA &&
      this.utils.isRbaEnabledBasedOnApp(loginOAuthParams.oauthConfig)
    );
  }

  getUrlBasedOnRba(
    loginOAuthParams: LoginOAuthParams,
    enableRBA = false,
    forcePromptLoginFlow = false
  ): string | null {
    const promptSuffix = forcePromptLoginFlow
      ? `&${loginOAuthParams.oauthConfig?.PROMPT}=${loginOAuthParams.oauthConfig?.PROMPT_VALUE}`
      : '';

    if (this.enableRbaCheck(enableRBA, loginOAuthParams)) {
      if (loginOAuthParams.oauthConfig.enableRBALearning) {
        return (
          this.getLoginAuthorizationUrl(loginOAuthParams, 'RBA_LEARNING') +
          promptSuffix
        );
      } else {
        return (
          this.getLoginAuthorizationUrl(loginOAuthParams, 'RBA_LOGIN') +
          promptSuffix
        );
      }
    } else {
      return null;
    }
  }

  getUrlBasedOnStorageValueHelper(
    loginOAuthParams: LoginOAuthParams,
    forcePromptLoginFlow = false
  ): string {
    if (forcePromptLoginFlow) {
      return this.getLoginAuthorizationUrl(loginOAuthParams, 'PROMPT');
    } else {
      return this.getLoginAuthorizationUrl(loginOAuthParams, 'DEFAULT');
    }
  }

  getLoginAuthorizationUrl(
    loginOAuthParams: LoginOAuthParams,
    operationType: LoginAuthorizationOperationTypes
  ): string {
    const initiateSsoSessionUrl = {
      RBA_LEARNING: this.utils.getPingServiceUrl(
        loginOAuthParams.oauthConfig,
        '',
        loginOAuthParams.pkceCode.codeChallenge
      ).loginOAuthUrlRbaLearning,
      RBA_LOGIN: this.utils.getPingServiceUrl(
        loginOAuthParams.oauthConfig,
        '',
        loginOAuthParams.pkceCode.codeChallenge
      ).loginOAuthUrlRbaLogin,
      PROMPT: this.utils.getPingServiceUrl(
        loginOAuthParams.oauthConfig,
        '',
        loginOAuthParams.pkceCode.codeChallenge
      ).loginOAuthUrlPersistantRememberMe,
      DEFAULT: this.utils.getPingServiceUrl(
        loginOAuthParams.oauthConfig,
        '',
        loginOAuthParams.pkceCode.codeChallenge
      ).loginOAuthUrlRememberMe
    };
    return initiateSsoSessionUrl[operationType];
  }
}
